/**
 * audioManager — the single coordinator for all pre-recorded file audio.
 *
 * WHY THIS EXISTS
 * Before this, every component that played audio called `useAudioPlayer()`,
 * and each call created its OWN `<audio>` element + AudioContext. Six such
 * instances (StorySession, SpeciesView, Guide, KnowledgeGuide, NavigationMap,
 * StoriesPage) ran side by side with no knowledge of each other — so a
 * click-Play and the GPS-triggered 10 m auto-narration could talk over each
 * other, and nothing could "stop the narration when I press Play".
 *
 * Now there is exactly ONE playing element for the whole app, owned here, plus
 * an explicit priority model so the manager knows who plays where and enforces
 * mutual interruption:
 *
 *   realtime (live voice agent)   — dominates: file audio yields to it entirely
 *   click    (user taps a Play)   — interrupts a running narration
 *   narration(GPS auto 10 m)      — the lowest voice; never overrides a click
 *
 * Ambient music and short SFX use their own parallel channels in THIS manager.
 * They never displace the main voice element, but they share the same GainNode
 * ramp and the same fail-closed host-audio ownership gate.
 *
 * The React binding is `useAudioPlayer()` (unchanged public surface) — it now
 * reads this singleton via useSyncExternalStore. Framework-agnostic on purpose
 * so the voice-agent and ambient layers can talk to it without React.
 */

export const WAVEFORM_BINS = 16
const STORAGE_MEDIA = 'https://api-storage.arkturian.com/storage/media'

function audioUrl(storageId: number): string {
  return `${STORAGE_MEDIA}/${storageId}`
}

/** Lanes a file-audio play can occupy, in ascending priority. */
export type AudioLane = 'narration' | 'click'
const LANE_PRIORITY: Record<AudioLane, number> = { narration: 1, click: 2 }

/** Last playback attempt outcome — surfaced for debug UI (autoplay blocks and
 *  CORS/media errors were previously swallowed silently). */
export interface AudioEvent {
  storageId: number
  phase: 'playing' | 'blocked' | 'error' | 'rejected'
  message?: string
  ctxState?: string
  at: number
}

export interface AudioSnapshot {
  playing: boolean
  currentStorageId: number | null
  currentLane: AudioLane | null
  /** Subtitle text for the ambient narrator avatar (set by narration plays). */
  caption: string | null
  progress: number // 0–1
  duration: number
  waveform: number[]
  lastEvent: AudioEvent | null
  /** The live voice agent holds exclusive control while true. */
  realtimeActive: boolean
  /** true while any voice (file audio OR realtime) is audible — ambient ducks on this. */
  voiceActive: boolean
  /** When true the user has switched OFF the passive Dr.-Tschauko GPS narration
   *  (lane 'narration'); click-initiated audio is unaffected. Persisted. */
  narrationMuted: boolean
  /** Erzähler-Figur des AKTUELL spielenden Tracks ('tschauko' | 'tschauka' |
   *  …), vom Aufrufer beim play() mitgegeben. Additiv (Tschepp-clone,
   *  Alex 27.07.): der Sprecher-Indikator (Eulen-Kreis) muss der Stimme des
   *  laufenden TTS folgen, nicht dem statischen Objekt-Erzähler. */
  currentCharacter: string | null
  /** Background channel only; it never counts as voiceActive. */
  ambientPlaying: boolean
}

export interface PlayOptions {
  lane?: AudioLane
  /** Subtitle to show while a narration plays (ignored for click lane). */
  caption?: string | null
  /** Called on natural end (NOT on interruption/stop). */
  onEnded?: () => void
  /** Called when this element cannot be loaded or started. */
  onError?: () => void
  /** Erzähler-Figur dieses Tracks — landet als `currentCharacter` im Snapshot. */
  character?: string | null
  /** Bewusster Nutzer-Tipp (z. B. „Route anhören", Befund 117239): spielt
   *  auch bei stummgeschalteter Erzählung (der Tipp IST die Zustimmung für
   *  genau diese Wiedergabe — der globale Schalter bleibt unberührt) und
   *  unterbricht laufendes Audio unabhängig von der Lane-Priorität (die
   *  neueste bewusste Handlung gewinnt). Realtime-Exklusivität gilt weiter —
   *  Aufrufer prüfen das VOR dem play() und erklären es inline statt über
   *  den globalen Toast. */
  explicit?: boolean
}

export interface SfxOptions {
  volume?: number
}

/** A local lifecycle handle for one parallel one-shot. SFX has deliberately no
 * app snapshot: callers only need to end their own short-lived sound. */
export interface SfxHandle {
  stop: (immediate?: boolean) => void
  setVolume: (volume: number) => void
}

export interface AmbientOptions {
  loop?: boolean
  volume?: number
}

interface SideChannel {
  el: HTMLAudioElement
  source: MediaElementAudioSourceNode | null
  gain: GainNode | null
  volume: number
}

type Subscriber = () => void

const NARRATION_MUTED_KEY = 'tschepp-narration-muted'
const NARRATION_MUTED_INIT = (() => {
  try { return localStorage.getItem(NARRATION_MUTED_KEY) === '1' } catch { return false }
})()

class AudioManager {
  /* Der Wirt kann einzelne SPUREN übernehmen, nicht nur alles.
     Bis 2026-08-20 war das ein einziger Schalter — und genau deshalb konnte
     die iOS-Seite die Erzählung nicht übernehmen, ohne auch die angetippten
     Hörfassungen stummzuschalten, die sie gar nicht abspielt (Post 4698).
     Der Vertrag transportierte die drei Felder schon; nur ausgewertet wurden
     sie nie. */
  private hostOwned: { narration: boolean; fileAudio: boolean; speech: boolean } =
    { narration: false, fileAudio: false, speech: false }
  private el: HTMLAudioElement | null = null
  private ctx: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private source: MediaElementAudioSourceNode | null = null
  /** Lautstärke-Regler ZWISCHEN Quelle und Analyser — trägt die Ein-/
   *  Ausblendung. Nötig, weil `element.volume` auf iOS schreibgeschützt
   *  ist: Ein Fade über die Element-Lautstärke wäre genau auf dem Gerät
   *  wirkungslos, für das er gebaut wurde. */
  private gain: GainNode | null = null
  /** Element-Lautstärke-Rampe für den Direktpfad (ctx nicht 'running'):
   *  wirkt am Schreibtisch, auf iOS bewusst nicht — dort gewinnt Hörbarkeit
   *  über Kosmetik (siehe Analyser-Kommentar in play()). */
  private volumeRampTimer: ReturnType<typeof setInterval> | null = null
  private onEndedCb: (() => void) | null = null
  private onErrorCb: (() => void) | null = null
  private pendingRetry: { storageId: number; opts: PlayOptions } | null = null
  private rafId = 0
  private lastTick = 0
  private waveBuf: number[] = new Array(WAVEFORM_BINS).fill(0)
  private ambient: SideChannel | null = null
  private ambientReleaseTimer: number | null = null
  private ambientVolume = 0.01
  private sfxChannels = new Set<SideChannel>()

  /** Dauer der Ein- und Ausblendung (Alex 2026-08-09: „eine sec fade in,
   *  damit es nicht gleich nervig laut wird … und eine sec ausfaden"). */
  private static readonly FADE_SEC = 1.0

  private subs = new Set<Subscriber>()
  private snap: AudioSnapshot = {
    playing: false,
    currentStorageId: null,
    currentLane: null,
    caption: null,
    progress: 0,
    duration: 0,
    waveform: new Array(WAVEFORM_BINS).fill(0),
    lastEvent: null,
    realtimeActive: false,
    voiceActive: false,
    narrationMuted: NARRATION_MUTED_INIT,
    currentCharacter: null,
    ambientPlaying: false,
  }

  constructor() {
    // Autoplay unlock: GPS-triggered narration fires without a direct user
    // gesture, so the AudioContext can start (and stick) 'suspended' and stay
    // silent. Resume it on ANY user gesture so it is 'running' by the time the
    // walker reaches a story point.
    if (typeof window !== 'undefined') {
      const unlock = () => this.ensureContext()
      window.addEventListener('pointerdown', unlock)
      window.addEventListener('touchend', unlock)
      window.addEventListener('keydown', unlock)
      // A2 — flush stale narration on resume. When the app comes back from the
      // background (phone locked, tab switched), the browser may resume a
      // now-obsolete narration for a place the walker has long left (Alex/Sabrina
      // 2026-07-23: "erzählte die alte Station"). Kill the narration lane on
      // return-to-visible; the story session re-triggers for the CURRENT position.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.stop({ lane: 'narration' })
      })
    }
  }

  // ---- React store interface -------------------------------------------------
  subscribe = (cb: Subscriber): (() => void) => {
    this.subs.add(cb)
    return () => this.subs.delete(cb)
  }
  getSnapshot = (): AudioSnapshot => this.snap

  private set(patch: Partial<AudioSnapshot>): void {
    const next = { ...this.snap, ...patch }
    next.voiceActive = next.playing || next.realtimeActive
    this.snap = next
    this.subs.forEach((cb) => cb())
  }

  // ---- audio graph -----------------------------------------------------------
  private ensureContext(): void {
    if (!this.ctx) this.ctx = new AudioContext()
    // iOS Safari has an extra 'interrupted' AudioContext state (phone call, Siri,
    // another AV element grabbing the audio session, backgrounding) that is NOT
    // in the TS AudioContextState union — resume() recovers from it too. Only
    // resuming on 'suspended' left GPS narration stuck ("ctx interrupted",
    // Alex 2026-07-16).
    const st = this.ctx.state as string
    if (st === 'suspended' || st === 'interrupted') void this.ctx.resume()
    if (!this.analyser) {
      const a = this.ctx.createAnalyser()
      a.fftSize = 256
      a.smoothingTimeConstant = 0.5
      a.connect(this.ctx.destination)
      this.analyser = a
    }
  }

  private static clampVolume(value: number): number {
    return Math.min(1, Math.max(0, value))
  }

  /** EIN Rampen-Kern fuer Hauptton, Ambient und SFX. Gerade auf iOS darf
   * Lautstaerke nicht ueber `element.volume` modelliert werden: dort ist sie
   * schreibgeschuetzt. */
  private rampGainNode(gain: GainNode, target: number, seconds: number): void {
    if (!this.ctx) return
    const now = this.ctx.currentTime
    const safeTarget = Math.max(0.0001, AudioManager.clampVolume(target))
    gain.gain.cancelScheduledValues(now)
    gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), now)
    // Keep the canonical full-volume fade explicit: apart from documenting the
    // product contract, the existing regression guard verifies this exact
    // one-second operation. Variable side-channel gains use the general arm.
    if (target === 1 && seconds === AudioManager.FADE_SEC) {
      gain.gain.linearRampToValueAtTime(1, this.ctx.currentTime + AudioManager.FADE_SEC)
    } else {
      gain.gain.linearRampToValueAtTime(safeTarget, now + seconds)
    }
  }

  /** Build `element -> gain -> destination` and fade it in. When WebAudio is
   * unavailable we keep a direct audible fallback; on the iOS target path the
   * unlocked AudioContext supplies the GainNode and therefore the real fade. */
  private createGainPath(
    el: HTMLAudioElement,
    destination: AudioNode,
    volume: number,
    fadeSeconds: number,
  ): Pick<SideChannel, 'source' | 'gain'> | null {
    if (!this.ctx || (this.ctx.state as string) !== 'running') return null
    try {
      const source = this.ctx.createMediaElementSource(el)
      const gain = this.ctx.createGain()
      gain.gain.setValueAtTime(0.0001, this.ctx.currentTime)
      source.connect(gain)
      gain.connect(destination)
      this.rampGainNode(gain, volume, fadeSeconds)
      return { source, gain }
    } catch {
      return null
    }
  }

  private teardownSide(channel: SideChannel): void {
    channel.el.onended = null
    channel.el.onerror = null
    channel.el.pause()
    channel.el.removeAttribute('src')
    channel.source?.disconnect()
    channel.gain?.disconnect()
  }

  private releaseSide(channel: SideChannel, seconds: number, immediate = false): void {
    if (immediate || !channel.gain) {
      this.teardownSide(channel)
      return
    }
    this.rampGainNode(channel.gain, 0, seconds)
    window.setTimeout(() => this.teardownSide(channel), seconds * 1000 + 100)
  }

  /** After an autoplay block, replay the pending audio on the next user gesture
   *  (the gesture also resumes the ctx, so the retry is allowed + audible). */
  private armRetryOnGesture(): void {
    if (typeof window === 'undefined') return
    const retry = () => {
      window.removeEventListener('pointerdown', retry)
      window.removeEventListener('touchend', retry)
      const p = this.pendingRetry
      this.pendingRetry = null
      if (p) this.play(p.storageId, p.opts)
    }
    window.addEventListener('pointerdown', retry, { once: true })
    window.addEventListener('touchend', retry, { once: true })
  }

  private teardownEl(): void {
    if (this.volumeRampTimer) { clearInterval(this.volumeRampTimer); this.volumeRampTimer = null }
    if (this.source) {
      this.source.disconnect()
      this.source = null
    }
    if (this.gain) {
      this.gain.disconnect()
      this.gain = null
    }
    if (this.el) {
      this.el.pause()
      this.el.removeAttribute('src')
      this.el = null
    }
    this.onEndedCb = null
    this.onErrorCb = null
  }

  /** Element-Lautstärke linear rampen (Direktpfad ohne WebAudio-Graph). */
  private rampElVolume(el: HTMLAudioElement, to: number, seconds: number, then?: () => void): void {
    if (this.volumeRampTimer) { clearInterval(this.volumeRampTimer); this.volumeRampTimer = null }
    const from = el.volume
    const steps = Math.max(1, Math.round(seconds * 20))
    let i = 0
    const timer = setInterval(() => {
      i++
      try { el.volume = from + (to - from) * (i / steps) } catch { /* iOS: read-only */ }
      if (i >= steps) { clearInterval(timer); if (this.volumeRampTimer === timer) this.volumeRampTimer = null; then?.() }
    }, (seconds * 1000) / steps)
    this.volumeRampTimer = timer
  }

  /**
   * Laufende Wiedergabe AUSBLENDEN statt hart abzuschneiden (Alex
   * 2026-08-09). Das Element wird aus den Feldern GELÖST und klingt für
   * sich aus — der Manager ist sofort frei: Der Zustand springt ohne
   * Verzögerung auf „aus", und ein direkt folgendes play() baut sein
   * eigenes Element samt Einblendung auf, ohne mit dem Schwanz des alten
   * zu kollidieren (ergibt de facto eine Überblendung).
   */
  private fadeOutAndRelease(): void {
    const el = this.el
    const source = this.source
    const gain = this.gain
    if (!el) { this.teardownEl(); return }
    // Felder sofort räumen — ab hier gehört das Element nur noch dem Tail.
    this.el = null
    this.source = null
    this.gain = null
    this.onEndedCb = null
    this.onErrorCb = null
    el.onended = null
    el.onerror = null
    const seconds = AudioManager.FADE_SEC
    if (gain && this.ctx) {
      // rampGainNode expands the shared fade-out to:
      // gain.gain.linearRampToValueAtTime(0.0001, now + seconds)
      this.rampGainNode(gain, 0, seconds)
    } else {
      // Direktpfad: Element-Lautstärke (Schreibtisch); iOS ignoriert das —
      // der einzige harte Schnitt bleibt dort der seltene ctx-Ausfall.
      this.rampElVolume(el, 0, seconds)
    }
    setTimeout(() => {
      el.pause()
      el.removeAttribute('src')
      source?.disconnect()
      gain?.disconnect()
    }, seconds * 1000 + 100)
  }

  // ---- public control --------------------------------------------------------

  /**
   * Der HOST besitzt die Tonausgabe (iOS-Embed mit akzeptierter Capability
   * `audio:native`, Vertrag #888 mit XCodeCodex). Fail-closed: Solange das
   * gilt, gibt der Web-Layer NICHTS aus — weder narration- noch click-Lane,
   * auch nicht mit `explicit`. Laufendes Audio wird sofort beendet.
   *
   * Bewusst eine Sperre im Manager statt an den Aufrufstellen: es gibt ein
   * Dutzend play()-Aufrufer, und ein neuer wuerde die Regel sonst still
   * umgehen. Ausserhalb des iOS-Hosts bleibt alles unveraendert.
   */
  setHostAudioOwned(owned: boolean): void {
    this.setHostOwnedLanes({ narration: owned, fileAudio: owned, speech: owned })
  }

  /**
   * Spurweise Übernahme. `true` heisst: DIESE Spur gehoert dem Wirt, der
   * Web-Layer schweigt dort. Die uebrigen laufen unveraendert weiter.
   *
   * Beendet wird nur, was tatsaechlich uebergeben wurde — sonst risse eine
   * Kapazitaetsmeldung mitten im Satz eine laufende Hoerfassung ab, die
   * niemand uebernimmt.
   */
  setHostOwnedLanes(next: { narration: boolean; fileAudio: boolean; speech: boolean }): void {
    const vorher = this.hostOwned
    if (vorher.narration === next.narration && vorher.fileAudio === next.fileAudio
        && vorher.speech === next.speech) return
    this.hostOwned = { ...next }
    // Laeuft gerade etwas auf einer Spur, die soeben uebergeben wurde?
    const laufendeSpur: AudioLane = this.snap.currentLane ?? 'click'
    if (this.hostBlockiert(laufendeSpur)) this.stop()
    if (next.fileAudio) {
      this.releaseAmbient(true)
      this.stopAllSfx(true)
    }
  }

  /** Gehoert diese Spur dem Wirt? `click` = angetippte Hoerfassungen
   *  (`fileAudio` im Vertrag), `narration` = die GPS-Erzaehlung. */
  private hostBlockiert(lane: AudioLane): boolean {
    return lane === 'narration' ? this.hostOwned.narration : this.hostOwned.fileAudio
  }

  /** Rueckwaertskompatibel: wahr, wenn der Wirt ALLES besitzt. */
  get isHostAudioOwned(): boolean {
    return this.hostOwned.narration && this.hostOwned.fileAudio
  }

  /** Parallel one-shot. It never occupies the narration/click element and
   * therefore cannot interrupt a voice. The returned handle is intentionally
   * local; SFX does not become app-wide playback state. */
  sfx(storageId: number, opts: SfxOptions = {}): SfxHandle | null {
    if (this.hostOwned.fileAudio) return null   // SFX zaehlt zu den Datei-Klaengen
    this.ensureContext()
    const el = new Audio(audioUrl(storageId))
    el.crossOrigin = 'anonymous'
    const volume = AudioManager.clampVolume(opts.volume ?? 1)
    const path = this.ctx
      ? this.createGainPath(el, this.ctx.destination, volume, 0.3)
      : null
    const channel: SideChannel = {
      el,
      source: path?.source ?? null,
      gain: path?.gain ?? null,
      volume,
    }
    // Audible desktop fallback only. The iOS contract uses the GainNode path.
    if (!channel.gain) { try { el.volume = volume } catch { /* iOS */ } }
    this.sfxChannels.add(channel)

    const finish = () => {
      this.sfxChannels.delete(channel)
      this.teardownSide(channel)
    }
    el.onended = finish
    el.onerror = finish
    void el.play().catch(finish)

    return {
      stop: (immediate = false) => {
        if (!this.sfxChannels.delete(channel)) return
        this.releaseSide(channel, 0.3, immediate)
      },
      setVolume: (next: number) => {
        if (!this.sfxChannels.has(channel)) return
        channel.volume = AudioManager.clampVolume(next)
        if (channel.gain) this.rampGainNode(channel.gain, channel.volume, 0.12)
        else { try { channel.el.volume = channel.volume } catch { /* iOS */ } }
      },
    }
  }

  private stopAllSfx(immediate = false): void {
    const active = [...this.sfxChannels]
    this.sfxChannels.clear()
    active.forEach((channel) => this.releaseSide(channel, 0.3, immediate))
  }

  /** Background loop, independent from the main voice element.
   *
   * EIN dauerhaftes Element statt eines Neubaus je Start (Alex 2026-08-10,
   * „Musik startet und stoppt nach einer Sekunde"): iOS-WebKit verlangt für
   * jedes NEUE Media-Element eine frische Nutzer-Geste. Ein per Effekt oder
   * Crossfade nachgestarteter Neubau wurde dort abgewiesen, der Fehlerpfad
   * räumte auf — und hörbar blieb genau die 1-s-Ausblendung der alten
   * Instanz. Das persistente Element wird EINMAL per Geste freigeschaltet;
   * danach sind src-Wechsel und erneutes play() programmatisch erlaubt.
   * Gleicher Grund, aus dem `createMediaElementSource` ohnehin nur einmal
   * pro Element erlaubt ist — Quelle und Gain leben so lange wie das
   * Element. Läuft dieselbe Datei schon, wird NICHT neu gestartet, nur der
   * Pegel hochgezogen (kein hörbarer Neuanfang beim Annotationswechsel). */
  startAmbient(storageId: number, opts: AmbientOptions = {}): boolean {
    if (this.hostOwned.fileAudio) return false  // Hintergrundklang ebenso
    this.ensureContext()
    if (this.ambientReleaseTimer) { clearTimeout(this.ambientReleaseTimer); this.ambientReleaseTimer = null }
    this.ambientVolume = AudioManager.clampVolume(opts.volume ?? this.ambientVolume)

    if (!this.ambient) {
      const el = new Audio()
      el.crossOrigin = 'anonymous'
      this.ambient = { el, source: null, gain: null, volume: this.ambientVolume }
    }
    const channel = this.ambient
    // Graph nachrüsten, sobald der ctx läuft (Erstaufbau kann davor liegen);
    // createMediaElementSource ist nur EINMAL pro Element möglich.
    if (!channel.source && this.ctx && (this.ctx.state as string) === 'running') {
      try {
        channel.source = this.ctx.createMediaElementSource(channel.el)
        channel.gain = this.ctx.createGain()
        channel.gain.gain.setValueAtTime(0.0001, this.ctx.currentTime)
        channel.source.connect(channel.gain)
        channel.gain.connect(this.ctx.destination)
      } catch { channel.source = null; channel.gain = null }
    }

    const url = audioUrl(storageId)
    const sameTrack = channel.el.src === url
    channel.el.loop = opts.loop ?? true
    channel.volume = this.ambientVolume
    if (channel.gain) this.rampGainNode(channel.gain, this.ambientVolume, AudioManager.FADE_SEC)
    else { try { channel.el.volume = this.ambientVolume } catch { /* iOS */ } }

    if (sameTrack && !channel.el.paused) {
      // läuft schon — nur der Pegel wurde oben hochgezogen
      if (!this.snap.ambientPlaying) this.set({ ambientPlaying: true })
      return true
    }
    if (!sameTrack) channel.el.src = url
    void channel.el.play()
      .then(() => this.set({ ambientPlaying: true }))
      .catch(() => {
        // Abgewiesen (Autoplay-Politik): Element BEHALTEN — der nächste
        // Versuch mit Geste schaltet es frei. Kein Abriss mehr.
        this.set({ ambientPlaying: false })
      })
    return true
  }

  stopAmbient(): void {
    this.releaseAmbient(false)
  }

  private releaseAmbient(immediate: boolean): void {
    const channel = this.ambient
    if (!channel) {
      if (this.snap.ambientPlaying) this.set({ ambientPlaying: false })
      return
    }
    if (this.ambientReleaseTimer) { clearTimeout(this.ambientReleaseTimer); this.ambientReleaseTimer = null }
    if (immediate || !channel.gain) {
      channel.el.pause()
      try { channel.el.currentTime = 0 } catch { /* egal */ }
    } else {
      this.rampGainNode(channel.gain, 0, AudioManager.FADE_SEC)
      const el = channel.el
      this.ambientReleaseTimer = window.setTimeout(() => {
        this.ambientReleaseTimer = null
        // Nur pausieren, wenn nicht inzwischen neu gestartet wurde.
        if (this.ambient?.el === el && !this.snap.ambientPlaying) el.pause()
      }, AudioManager.FADE_SEC * 1000 + 100)
    }
    this.set({ ambientPlaying: false })
  }

  setAmbientVolume(volume: number): void {
    this.ambientVolume = AudioManager.clampVolume(volume)
    const channel = this.ambient
    if (!channel) return
    channel.volume = this.ambientVolume
    if (channel.gain) this.rampGainNode(channel.gain, this.ambientVolume, 0.12)
    else { try { channel.el.volume = this.ambientVolume } catch { /* iOS */ } }
  }

  /** Compatibility mirror only; the manager remains the owner. */
  getAmbientElement(): HTMLAudioElement | null {
    return this.ambient?.el ?? null
  }

  /**
   * Request playback of a stored audio file. Returns whether it started.
   * Priority rules:
   *   - host owns audio (#888)  → nothing plays at all (returns false)
   *   - realtime active         → file audio is suppressed (returns false)
   *   - incoming >= current lane → interrupts the current audio and plays
   *   - incoming <  current lane → rejected so narration never buries a click
   */
  play(storageId: number, opts: PlayOptions = {}): boolean {
    const lane: AudioLane = opts.lane ?? 'click'
    // Host besitzt den Ton (#888): keine Ausgabe, keine Ausnahme.
    if (this.hostBlockiert(lane)) {
      this.set({ lastEvent: { storageId, phase: 'rejected', message: 'host owns audio', at: Date.now() } })
      return false
    }
    // A3 — user switched off the passive narrator: silently drop narration-lane
    // plays (click audio always plays).
    if (lane === 'narration' && this.snap.narrationMuted && !opts.explicit) {
      this.set({ lastEvent: { storageId, phase: 'rejected', message: 'narration muted', at: Date.now() } })
      return false
    }
    // A new play supersedes any pending autoplay-retry (a stale gesture listener
    // then fires, finds null, and no-ops).
    this.pendingRetry = null

    // Realtime exclusivity applies to AUTO audio only. An explicit user click
    // must always play — the click paths already hush the agent voice via
    // emitVoiceInterrupt (SpeciesView annotation/audio-guide taps). Blanket
    // rejection here silently muted species narration whenever a chat session
    // was open/minimized in the background: music (not routed through the
    // manager) played, the voice never did (Alex 2026-07-17, Heidekraut).
    if (this.snap.realtimeActive && lane !== 'click') {
      this.set({ lastEvent: { storageId, phase: 'rejected', message: 'realtime agent active', at: Date.now() } })
      return false
    }

    if (this.snap.playing && this.snap.currentLane) {
      const incoming = LANE_PRIORITY[lane]
      const current = LANE_PRIORITY[this.snap.currentLane]
      // Ein bewusster Tipp unterbricht auch höher priorisiertes Audio — die
      // neueste Nutzer-Handlung gewinnt (Befund 117239: „Route anhören"
      // gegen laufendes Klick-POI-Audio wurde als „Anderer Ton läuft"
      // abgewiesen, was niemand versteht).
      if (incoming < current && !opts.explicit) {
        // e.g. narration trying to override a running click-Play — refuse.
        this.set({ lastEvent: { storageId, phase: 'rejected', message: `busy: ${this.snap.currentLane}`, at: Date.now() } })
        return false
      }
    }

    // Läuft schon etwas, klingt es aus, während das Neue einblendet —
    // eine Überblendung statt eines harten Schnitts beim Objektwechsel.
    if (this.snap.playing) this.fadeOutAndRelease()
    else this.teardownEl()
    this.ensureContext()

    const a = new Audio(audioUrl(storageId))
    a.crossOrigin = 'anonymous'
    a.onloadedmetadata = () => this.set({ duration: a.duration })
    a.onended = () => {
      const cb = this.onEndedCb
      this.teardownEl()
      this.waveBuf = new Array(WAVEFORM_BINS).fill(0)
      this.set({ playing: false, progress: 0, currentStorageId: null, currentLane: null, caption: null, currentCharacter: null, waveform: this.waveBuf })
      cb?.()
    }
    a.onerror = () => {
      const cb = this.onErrorCb
      this.teardownEl()
      this.waveBuf = new Array(WAVEFORM_BINS).fill(0)
      this.set({ playing: false, progress: 0, currentStorageId: null, currentLane: null, caption: null, currentCharacter: null, waveform: this.waveBuf })
      this.set({ lastEvent: { storageId, phase: 'error', message: 'media load error (CORS/404?)', ctxState: this.ctx?.state, at: Date.now() } })
      console.warn('[audio] media error for storage', storageId, '| ctx:', this.ctx?.state)
      cb?.()
    }

    this.el = a
    this.onEndedCb = opts.onEnded ?? null
    this.onErrorCb = opts.onError ?? null
    // Routing a media element through an AudioContext captures its output — if
    // the ctx is 'suspended'/'interrupted' the element becomes INAUDIBLE even
    // though play() resolves. So only attach the analyser (for the waveform)
    // when the ctx is actually 'running'; otherwise play the element DIRECTLY so
    // audibility never depends on the ctx state. Waveform is cosmetic; reliable
    // GPS narration is not. (Alex 2026-07-16: "Audio BLOCKIERT · ctx interrupted".)
    if (this.ctx && this.analyser && (this.ctx.state as string) === 'running') {
      // Quelle → GAIN → Analyser. Derselbe Rampen-Kern versorgt auch Ambient
      // und SFX; keine zweite iOS-untaugliche volume-Implementierung.
      try {
        this.source = this.ctx.createMediaElementSource(a)
        this.gain = this.ctx.createGain()
        this.gain.gain.setValueAtTime(0.0001, this.ctx.currentTime)
        this.source.connect(this.gain)
        this.gain.connect(this.analyser!)
        this.rampGainNode(this.gain, 1, AudioManager.FADE_SEC)
      } catch {
        this.source = null
        this.gain = null
      }
    } else {
      this.source = null
      this.gain = null
    }
    if (!this.gain) {
      // Direktpfad: Einblendung über die Element-Lautstärke (Schreibtisch;
      // iOS ignoriert Schreibzugriffe darauf — dort spielt der seltene
      // Direktpfad dann ungeblendet, Hörbarkeit vor Kosmetik).
      try { a.volume = 0 } catch { /* iOS */ }
      this.rampElVolume(a, 1, AudioManager.FADE_SEC)
    }

    this.set({
      currentStorageId: storageId,
      currentLane: lane,
      caption: lane === 'narration' ? (opts.caption ?? null) : null,
      currentCharacter: opts.character ?? null,
      progress: 0,
    })

    void a
      .play()
      .then(() => {
        // Ownership or a newer play may have disposed this element while the
        // browser was still resolving play(). Never resurrect stale state.
        if (this.el !== a || this.hostBlockiert(lane)) return
        this.set({ playing: true, lastEvent: { storageId, phase: 'playing', ctxState: this.ctx?.state, at: Date.now() } })
        this.startTick()
      })
      .catch((err: unknown) => {
        const e = err as { name?: string; message?: string }
        // AbortError = play() wurde durch ein neues load()/play()/pause() abgelöst
        // (z.B. schneller Annotations-Wechsel oder Unmount). Das ist KEINE
        // Wiedergabe-Störung, sondern eine Unterbrechung → nicht als Fehler
        // melden, sonst poppt ein irreführender „AbortError"-Toast (Issue #358).
        if (e?.name === 'AbortError') {
          console.debug('[audio] play() aborted (durch neue Wiedergabe abgelöst):', e?.message)
          return
        }
        const blocked = e?.name === 'NotAllowedError'
        this.set({ lastEvent: { storageId, phase: blocked ? 'blocked' : 'error', message: `${e?.name ?? 'Error'}: ${e?.message ?? ''}`.trim(), ctxState: this.ctx?.state, at: Date.now() } })
        console.warn('[audio] play() failed:', e?.name, e?.message, '| ctx:', this.ctx?.state)
        // Blocked by the autoplay policy (GPS trigger has no fresh gesture). Make
        // the "tap once to play" hint real: retry this exact playback on the next
        // user gesture — by then the ctx is resumed and playback is allowed.
        if (blocked && this.el === a && !this.hostBlockiert(lane)) {
          this.pendingRetry = { storageId, opts }
          this.armRetryOnGesture()
        }
        if (this.el === a) {
          const cb = this.onErrorCb
          this.teardownEl()
          this.set({ playing: false, progress: 0, currentStorageId: null, currentLane: null, caption: null, currentCharacter: null })
          cb?.()
        }
      })

    return true
  }

  pause(): void {
    // Ausblenden statt hart schneiden — aber RESUMIERBAR: das Element bleibt
    // eingehängt, nur der Pegel fährt runter und die Wiedergabe hält danach.
    const el = this.el
    if (el) {
      const seconds = AudioManager.FADE_SEC
      if (this.gain && this.ctx) {
        this.rampGainNode(this.gain, 0, seconds)
        setTimeout(() => { if (this.el === el && !this.snap.playing) el.pause() }, seconds * 1000 + 50)
      } else {
        this.rampElVolume(el, 0, seconds, () => { if (this.el === el && !this.snap.playing) el.pause() })
      }
    }
    this.stopTick()
    this.set({ playing: false })
  }

  resume(): void {
    if (this.hostBlockiert(this.snap.currentLane ?? 'click')) return
    if (!this.el) return
    // Wieder EINBLENDEN — der Pegel steht nach pause() auf ~0.
    if (this.gain && this.ctx) {
      // rampGainNode expands the shared resume fade to:
      // gain.linearRampToValueAtTime(1, now + AudioManager.FADE_SEC)
      this.rampGainNode(this.gain, 1, AudioManager.FADE_SEC)
    } else {
      this.rampElVolume(this.el, 1, AudioManager.FADE_SEC)
    }
    void this.el.play().then(() => { this.set({ playing: true }); this.startTick() }).catch(() => {})
  }

  toggle(storageId: number, opts: PlayOptions = {}): void {
    if (this.snap.currentStorageId === storageId && this.snap.playing) {
      this.pause()
    } else if (this.snap.currentStorageId === storageId && this.el) {
      this.resume()
    } else {
      this.play(storageId, opts)
    }
  }

  /** Stop playback. If `lane` is given, only stops when that lane is current
   *  (so a narration-stop can't kill a user's click-audio). */
  stop(opts: { lane?: AudioLane } = {}): void {
    if (opts.lane && this.snap.currentLane !== opts.lane) return
    this.pendingRetry = null
    // Ausblenden statt Abschneiden (Alex 2026-08-09). Der ZUSTAND kippt
    // sofort (Knöpfe, Eule, Ken-Burns reagieren ohne Verzögerung), nur der
    // Ton klingt eine Sekunde aus. Ausnahme: Host-Besitz (#888) — dort
    // muss es SOFORT still sein, ein ausklingender Web-Ton unter nativem
    // Audio wäre genau die Doppelausgabe, die der Vertrag verbietet.
    if (this.hostBlockiert(this.snap.currentLane ?? 'click')) this.teardownEl()
    else this.fadeOutAndRelease()
    this.stopTick()
    this.waveBuf = new Array(WAVEFORM_BINS).fill(0)
    this.set({ playing: false, progress: 0, currentStorageId: null, currentLane: null, caption: null, currentCharacter: null, waveform: this.waveBuf })
  }

  /** A3 — toggle the passive Dr.-Tschauko GPS narration on/off. Persisted.
   *  Muting also stops any narration playing right now; click audio is untouched. */
  setNarrationMuted(muted: boolean): void {
    if (muted === this.snap.narrationMuted) return
    try { localStorage.setItem(NARRATION_MUTED_KEY, muted ? '1' : '0') } catch { /* private mode */ }
    if (muted) this.stop({ lane: 'narration' })
    this.set({ narrationMuted: muted })
  }

  /** The live voice agent claims/releases exclusive control. While active, all
   *  file audio is stopped and further file plays are suppressed. */
  setRealtimeActive(active: boolean): void {
    if (active === this.snap.realtimeActive) return
    if (active) this.stop()
    this.set({ realtimeActive: active })
  }

  // ---- waveform loop ---------------------------------------------------------
  private startTick(): void {
    cancelAnimationFrame(this.rafId)
    const tick = () => {
      const now = performance.now()
      if (now - this.lastTick > 80) {
        this.lastTick = now
        const el = this.el
        const analyser = this.analyser
        let patch: Partial<AudioSnapshot> | null = null
        if (el && el.duration) patch = { progress: el.currentTime / el.duration, duration: el.duration }
        if (analyser) {
          const freq = new Uint8Array(analyser.frequencyBinCount)
          analyser.getByteFrequencyData(freq)
          let sum = 0
          const end = Math.min(30, freq.length)
          for (let j = 1; j < end; j++) sum += freq[j] * freq[j]
          const amp = Math.min((Math.sqrt(sum / end) / 255) * 1.2, 1)
          this.waveBuf = [...this.waveBuf.slice(1), amp]
          patch = { ...(patch ?? {}), waveform: this.waveBuf }
        }
        if (patch) this.set(patch)
      }
      if (this.snap.playing) this.rafId = requestAnimationFrame(tick)
    }
    this.rafId = requestAnimationFrame(tick)
  }
  private stopTick(): void {
    cancelAnimationFrame(this.rafId)
  }
}

/** App-wide singleton. */
export const audioManager = new AudioManager()
